import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from "@angular/core";
import {Api, ApiService} from "../../../../../common/services/api.service";
import {ToastService} from "../../../../../common/services/toast.service";
import {SpinnerService} from "../../../../../common/services/spinner.service";
import {StorageService} from "../../../../../common/services/storage.service";
import {User} from "../../../../../common/interfaces/user.interface";
import {Base} from "../../../../../common/interfaces/base.interfaces";
import {ConfirmComponent} from "../../../../../common/components/confirm/confirm.component";

@Component({
    selector: "section-trusted-devices",
    templateUrl: "trusted-devices.component.html",
    styleUrls: [
        "trusted-devices.component.scss"
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TrustedDevicesComponent implements OnInit, OnDestroy {

    /**
     * Component destroy event emitter
     * @type {EventEmitter<boolean>}
     */
    private destroy$: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild(ConfirmComponent, {static: false})
    public confirmRef: ConfirmComponent;

    @Input()
    public user: User.IData;

    @Input()
    public state: Base.IState;

    public displayedColumns: string[] = ["device", "label", "last_used", "created", "actions"];

    public dataSource: any[];

    public loading: boolean = false;

    public constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private apiService: ApiService,
        private toastService: ToastService,
        private spinnerService: SpinnerService,
        private storageService: StorageService
    ) {
    }

    /**
     * Load the list of trusted devices for the current user
     * @returns {Promise<any>}
     */
    private async getTrustedDevices(): Promise<any> {
        this.loading = true;
        // Send this device's token so the server can flag which row is the current device
        const deviceToken: string = this.storageService.getDeviceToken(this.user && this.user.email);
        if (deviceToken) {
            this.apiService.setHeaders({"X-Device-Token": deviceToken});
        }
        const {data}: Api.IResponse = await this.apiService.request(Api.EMethod.Get,
            ["user", "trusted_devices"]);
        this.loading = false;

        if (data) {
            this.dataSource = data;
        } else {
            this.dataSource = [];
        }

        this.changeDetectorRef.markForCheck();
    }

    /**
     * Revoke a single trusted device
     * @param {any} device
     * @returns {Promise<any>}
     */
    public async revokeDevice(device: any): Promise<any> {
        if (!await this.confirmRef.confirm("Revoke this device? It will need 2FA on next login.")) {
            return;
        }
        this.spinnerService.show();
        const {type, message}: Api.IResponse = await this.apiService.request(Api.EMethod.Delete,
            ["user", "trusted_devices", String(device.id)]);
        this.spinnerService.hide();
        if (type as string === "success") {
            this.toastService.show(message, "success");
            // If we revoked the device we are currently on, drop its local token too
            if (device.is_current) {
                this.storageService.clearPersistent("device_token");
            }
            this.getTrustedDevices();
        }
    }

    /**
     * Revoke every trusted device for the current user
     * @returns {Promise<any>}
     */
    public async revokeAll(): Promise<any> {
        if (!await this.confirmRef.confirm("Revoke all trusted devices? Every device will need 2FA on next login.")) {
            return;
        }
        this.spinnerService.show();
        const {type, message}: Api.IResponse = await this.apiService.request(Api.EMethod.Delete,
            ["user", "trusted_devices"]);
        this.spinnerService.hide();
        if (type as string === "success") {
            this.toastService.show(message, "success");
            this.storageService.clearPersistent("device_token");
            this.getTrustedDevices();
        }
    }

    public ngOnInit(): void {
        this.getTrustedDevices();
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

}